import { useState, useRef, useEffect, useMemo } from "react";
import { ChevronDown, Search, Star, Sparkles, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import type { LLMSettings } from "@/types";
import { Input } from "@/components/ui/input";

interface Model {
  id: string;
  name: string;
  provider: string;
  providerType: LLMSettings["provider"];
  description?: string;
  isFavorite?: boolean;
}

// Popular models for each provider
const MODELS: Model[] = [
  // Ollama models
  { id: "llama3.2", name: "Llama 3.2", provider: "Ollama", providerType: "ollama", description: "Fast and efficient" },
  { id: "llama3.1", name: "Llama 3.1", provider: "Ollama", providerType: "ollama", description: "Latest Llama model" },
  { id: "mistral", name: "Mistral", provider: "Ollama", providerType: "ollama", description: "Great performance" },
  { id: "codellama", name: "Code Llama", provider: "Ollama", providerType: "ollama", description: "Optimized for code" },
  { id: "phi3", name: "Phi 3", provider: "Ollama", providerType: "ollama", description: "Microsoft's small model" },
  { id: "gemma2", name: "Gemma 2", provider: "Ollama", providerType: "ollama", description: "Google's open model" },
  
  // OpenAI-compatible
  { id: "gpt-4o", name: "GPT-4o", provider: "OpenAI", providerType: "openai-compatible", description: "Most capable" },
  { id: "gpt-4o-mini", name: "GPT-4o Mini", provider: "OpenAI", providerType: "openai-compatible", description: "Fast and affordable" },
  { id: "gpt-4-turbo", name: "GPT-4 Turbo", provider: "OpenAI", providerType: "openai-compatible", description: "Latest GPT-4" },
  { id: "gpt-3.5-turbo", name: "GPT-3.5 Turbo", provider: "OpenAI", providerType: "openai-compatible", description: "Fast and reliable" },
  { id: "claude-3-5-sonnet", name: "Claude 3.5 Sonnet", provider: "Anthropic", providerType: "openai-compatible", description: "Excellent reasoning" },
  { id: "claude-3-opus", name: "Claude 3 Opus", provider: "Anthropic", providerType: "openai-compatible", description: "Most capable Claude" },
  { id: "claude-3-haiku", name: "Claude 3 Haiku", provider: "Anthropic", providerType: "openai-compatible", description: "Fast responses" },
  
  // Cloudflare Workers AI
  { id: "@cf/meta/llama-3.1-8b-instruct", name: "Llama 3.1 8B", provider: "Cloudflare", providerType: "cloudflare-workers-ai", description: "Fast inference" },
  { id: "@cf/meta/llama-3.1-70b-instruct", name: "Llama 3.1 70B", provider: "Cloudflare", providerType: "cloudflare-workers-ai", description: "More capable" },
  { id: "@cf/mistral/mistral-7b-instruct-v0.1", name: "Mistral 7B", provider: "Cloudflare", providerType: "cloudflare-workers-ai", description: "Efficient model" },
  { id: "@cf/qwen/qwen1.5-14b-chat-awq", name: "Qwen 1.5 14B", provider: "Cloudflare", providerType: "cloudflare-workers-ai", description: "Multilingual" },
];

interface ModelSelectorProps {
  currentModel: string;
  currentProvider: LLMSettings["provider"];
  onModelChange: (model: string, provider: LLMSettings["provider"]) => void;
  onOpenAccount?: () => void;
  className?: string;
}

export const ModelSelector = ({
  currentModel,
  currentProvider,
  onModelChange,
  onOpenAccount,
  className,
}: ModelSelectorProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [favorites, setFavorites] = useState<string[]>(() => {
    const stored = localStorage.getItem("favorite-models");
    return stored ? JSON.parse(stored) : [];
  });
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleEscape);
      // Focus search input when opened
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen]);

  // Filter models based on search and current provider
  const filteredModels = useMemo(() => {
    const query = searchQuery.toLowerCase();
    return MODELS.filter(model => {
      const matchesSearch = 
        model.name.toLowerCase().includes(query) ||
        model.provider.toLowerCase().includes(query) ||
        model.description?.toLowerCase().includes(query);
      
      // Show all models if searching, otherwise filter by current provider
      return searchQuery ? matchesSearch : model.providerType === currentProvider;
    });
  }, [searchQuery, currentProvider]);

  // Separate favorites
  const favoriteModels = filteredModels.filter(m => favorites.includes(m.id));
  const otherModels = filteredModels.filter(m => !favorites.includes(m.id));

  // Find current model info
  const currentModelInfo = MODELS.find(m => m.id === currentModel) || {
    name: currentModel,
    provider: currentProvider === "ollama" ? "Ollama" : currentProvider === "cloudflare-workers-ai" ? "Cloudflare" : "OpenAI",
    providerType: currentProvider,
  };

  const toggleFavorite = (modelId: string) => {
    const newFavorites = favorites.includes(modelId)
      ? favorites.filter(id => id !== modelId)
      : [...favorites, modelId];
    setFavorites(newFavorites);
    localStorage.setItem("favorite-models", JSON.stringify(newFavorites));
  };

  const handleModelSelect = (model: Model) => {
    onModelChange(model.id, model.providerType);
    setIsOpen(false);
    setSearchQuery("");
  };

  return (
    <div className={cn("relative", className)} ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1.5 sm:gap-2 rounded-lg border border-border/60 bg-white/70 px-2 py-1.5 sm:px-3 sm:py-2 text-xs sm:text-sm font-medium text-foreground shadow-sm transition hover:bg-white hover:shadow dark:bg-white/5 dark:hover:bg-white/10 min-w-0"
      >
        <Sparkles className="hidden sm:inline h-3.5 w-3.5 sm:h-4 sm:w-4 text-primary flex-shrink-0" />
        <div className="flex flex-col items-start min-w-0 max-w-[120px] sm:max-w-[200px]">
          <span className="truncate font-semibold text-[9px] sm:text-xs">{currentModelInfo.name}</span>
          <span className="truncate text-[8px] sm:text-[10px] text-muted-foreground">{currentModelInfo.provider}</span>
        </div>
        <ChevronDown className={cn("h-3 w-3 sm:h-3.5 sm:w-3.5 text-muted-foreground transition-transform flex-shrink-0", isOpen && "rotate-180")} />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute top-full left-0 right-0 left-auto sm:right-auto lg:w-80 mt-2 rounded-xl border border-border/60 bg-white/95 shadow-2xl dark:bg-slate-900 z-50 max-h-[70vh] flex flex-col backdrop-blur-sm">
          {/* Search */}
          <div className="p-3 border-b border-border/40">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                ref={searchInputRef}
                type="text"
                placeholder="Search models..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8 h-8 text-xs"
              />
            </div>
            {searchQuery && (
              <p className="mt-2 text-[10px] text-muted-foreground">
                Showing {filteredModels.length} model{filteredModels.length !== 1 ? 's' : ''}
              </p>
            )}
          </div>

          {/* Model List */}
          <div className="overflow-y-auto max-h-96 p-2">
            {/* Favorites */}
            {favoriteModels.length > 0 && (
              <div className="mb-3">
                <div className="flex items-center gap-1.5 px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                  <Star className="h-3 w-3" />
                  Favorites
                </div>
                <div className="space-y-0.5">
                  {favoriteModels.map(model => (
                    <ModelItem
                      key={model.id}
                      model={model}
                      isSelected={model.id === currentModel}
                      isFavorite={favorites.includes(model.id)}
                      onSelect={handleModelSelect}
                      onToggleFavorite={toggleFavorite}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Other Models */}
            {otherModels.length > 0 && (
              <div>
                {favoriteModels.length > 0 && (
                  <div className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                    All Models
                  </div>
                )}
                <div className="space-y-0.5">
                  {otherModels.map(model => (
                    <ModelItem
                      key={model.id}
                      model={model}
                      isSelected={model.id === currentModel}
                      isFavorite={favorites.includes(model.id)}
                      onSelect={handleModelSelect}
                      onToggleFavorite={toggleFavorite}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* No results */}
            {filteredModels.length === 0 && (
              <div className="py-8 text-center">
                <p className="text-sm text-muted-foreground">No models found</p>
                <p className="text-xs text-muted-foreground mt-1">Try a different search term</p>
              </div>
            )}
          </div>

          {/* Footer hint */}
          <div className="border-t border-border/40 p-2 text-[10px] text-muted-foreground text-center">
            Configure providers in <button className="text-primary hover:underline" onClick={() => { setIsOpen(false); onOpenAccount?.(); }}>Account Settings</button>
          </div>
        </div>
      )}
    </div>
  );
};

interface ModelItemProps {
  model: Model;
  isSelected: boolean;
  isFavorite: boolean;
  onSelect: (model: Model) => void;
  onToggleFavorite: (modelId: string) => void;
}

const ModelItem = ({ model, isSelected, isFavorite, onSelect, onToggleFavorite }: ModelItemProps) => (
  <div
    className={cn(
      "flex items-center gap-2 rounded-lg px-2 py-2 transition-colors group cursor-pointer",
      isSelected ? "bg-primary/10 text-primary" : "hover:bg-muted"
    )}
    onClick={() => onSelect(model)}
  >
    {/* Star favorite button */}
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onToggleFavorite(model.id);
      }}
      className="flex-shrink-0 p-0.5 hover:bg-background/50 rounded transition-colors"
    >
      <Star
        className={cn(
          "h-3.5 w-3.5 transition-colors",
          isFavorite ? "fill-yellow-500 text-yellow-500" : "text-muted-foreground/50 group-hover:text-muted-foreground"
        )}
      />
    </button>

    {/* Model info */}
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2">
        <p className="text-xs font-medium truncate">{model.name}</p>
        {isSelected && <Check className="h-3 w-3 flex-shrink-0" />}
      </div>
      <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
        <span className="truncate">{model.provider}</span>
        {model.description && (
          <>
            <span>•</span>
            <span className="truncate">{model.description}</span>
          </>
        )}
      </div>
    </div>
  </div>
);
